import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import {
  TitleSection,
  NonInteractiveVisualizationWrapper,
} from '@mentimeter/question-modules-shared';
import type { ScalesT } from '@mentimeter/question-modules-types';
import { useRef } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { useRagnar } from '@mentimeter/ragnar-react';
import { stepToDisplayValue } from './numerical-scale/transformValues';
import type { CustomDataT } from './types';
import { useViewSizeChange } from './hooks';
import { NumericalScale } from './numerical-scale/NumericalScale';

// We have the same type for controls, copy pasted for now
// Needs to be a type instead of interface as the interface can't satisfy the generic used with useSlideState hook
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type SlideStateNumericalQuestion = { showCorrect?: boolean };

type NumericalQuestionT = ScalesT;

const DEFAULT_CUSTOM_DATA = {
  correct_answer: 7,
  error_margin: 1,
  has_error_margin: false,
  scale: 1,
  display_min: 0,
} as CustomDataT;

export function NumericalQuestionPresentation() {
  const {
    useQuestion,
    useResults,
    useSlideState,
    useConfig,
    InteractiveCanvasComponents,
  } = usePresentationContext();
  const question = useQuestion();
  const { results = [] } = useResults<NumericalQuestionT>();
  const contentWidthRef = useRef<HTMLDivElement | null>(null);
  const [{ showCorrect }] = useSlideState<SlideStateNumericalQuestion>();
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
    },
  } = useRagnar();
  const { mode } = useConfig();
  const shouldShowCorrect =
    showCorrect || mode === 'idle' || mode === 'screenshot';
  const isScreenshotMode = mode === 'screenshot';
  const { size, container } = useViewSizeChange(question);

  const {
    correct_answer: correctAnswer,
    error_margin: errorMargin,
    has_error_margin: hasErrorMargin,
    display_min: displayMin,
    scale,
  } = (question.module_custom_data as CustomDataT) || DEFAULT_CUSTOM_DATA;

  const { max } = question.range;

  const displayMax = stepToDisplayValue({
    step: max,
    scale,
    min: displayMin,
  });

  const ConditionalWrapper =
    InteractiveCanvasComponents?.CanvasVisualizationArea
      ? InteractiveCanvasComponents?.CanvasVisualizationArea
      : NonInteractiveVisualizationWrapper;

  return (
    <Box width="100%" height="100%" data-testid="numerical-question-container">
      <TitleSection
        question={question.question}
        questionStyled={question.question_styled}
        question_description={question.question_description}
        title_meta={question.title_meta}
        layout={question.layout}
        showTextSizeDropdown={true}
      />
      <ConditionalWrapper
        width="100%"
        height="100%"
        flex="1 1 auto"
        alignItems="center"
        justifyContent="center"
        ref={contentWidthRef}
      >
        <Box
          width="100%"
          justifyContent="center"
          alignItems="center"
          flexDirection="row"
        >
          <Box style={{ flexWrap: 'wrap' }}>
            <Text
              fontSize="200"
              fontWeight="semiBold"
              data-testid="presentation-range-min-value"
            >
              {displayMin}
            </Text>
          </Box>
          <Box width="60%" height="550px" px={2} zIndex={1}>
            {question.choices[0] && (
              <div
                ref={container}
                style={{
                  width: '100%',
                  height: '100%',
                }}
                // For size changes, force re-render the visualization
                key={`${size.width}-${size.height}`}
              >
                <NumericalScale
                  baseFontSize={12}
                  textColor={textColor}
                  data={results[0]?.score}
                  fills={fillColors}
                  lineColor={textColor}
                  showCorrect={shouldShowCorrect}
                  hasErrorMargin={hasErrorMargin}
                  displayMin={displayMin}
                  maxStep={max}
                  errorMargin={errorMargin}
                  correctAnswer={correctAnswer}
                  isScreenshotMode={isScreenshotMode}
                  scale={scale}
                />
              </div>
            )}
          </Box>
          <Box style={{ flexWrap: 'wrap' }}>
            <Text
              fontSize="200"
              fontWeight="semiBold"
              data-testid="presentation-range-max-value"
            >
              {displayMax}
            </Text>
          </Box>
        </Box>
      </ConditionalWrapper>
    </Box>
  );
}
